import { useCallback, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { AgeDeclarationOverlay, resolveTheme } from "@heraion/ui";
import { apiFetch } from "./api";
import { useAuth } from "./auth/AuthContext";
import { deleteAccount } from "./auth/deleteAccount";
import { useThemePalette } from "./theme";
import { useShellProfile } from "./ThemeProfileBridge";

// Thin mount of the shared age-declaration overlay (#175); twin of
// apps/mobile/src/screens/AgeGate.tsx. Mounted inside ThemeProfileBridge: it
// reads the shell's one profile doc (no second GET). Once the declaration is
// recorded it is marked in the shell profile and the doc re-read in the
// background (ThemeProfileBridge weighs the two). A 404's empty doc or a
// failed read ask nothing. The ways out are the account menu's own sign-out and
// Settings' own delete-account flow (#383). A withdrawal there brings it back.
export function AgeGate({ children }: { children: ReactNode }) {
  const { signOut } = useAuth();
  const { t } = useTranslation();
  const { dark, family } = useThemePalette();
  const { refresh, ageDeclared, noteAgeDeclared } = useShellProfile();
  const onDeclared = useCallback(() => {
    noteAgeDeclared();
    void refresh();
  }, [noteAgeDeclared, refresh]);
  const onDeleteAccount = useCallback(
    (onBusy: (busy: boolean) => void) => deleteAccount({ t, signOut, onBusy }),
    [t, signOut],
  );

  return (
    <AgeDeclarationOverlay
      theme={resolveTheme(dark, family)}
      required={ageDeclared === false}
      apiFetch={apiFetch}
      onDeclared={onDeclared}
      onSignOut={signOut}
      onDeleteAccount={onDeleteAccount}
    >
      {children}
    </AgeDeclarationOverlay>
  );
}
